(function () {
  'use strict';

  var root = document.getElementById('ShamelessCartDrawer');
  if (!root) return;

  var itemsEl = root.querySelector('[data-scd-items]');
  var emptyEl = root.querySelector('[data-scd-empty]');
  var footerEl = root.querySelector('[data-scd-footer]');
  var subtotalEl = root.querySelector('[data-scd-subtotal]');
  var shippingEl = root.querySelector('[data-scd-shipping]');
  var shippingTextEl = root.querySelector('[data-scd-shipping-text]');
  var shippingProgressEl = root.querySelector('[data-scd-shipping-progress]');
  var recsEl = root.querySelector('[data-scd-recs]');
  var recsTrack = root.querySelector('[data-scd-recs-track]');
  var variantLayer = root.querySelector('[data-scd-variant-layer]');
  var variantProductEl = root.querySelector('[data-scd-variant-product]');
  var variantOptionsEl = root.querySelector('[data-scd-variant-options]');

  var lastFocused = null;
  var currentCart = null;
  var recSeedProductId = null;
  var recRequestToken = 0;
  var recommendationProducts = {};
  var productJsonCache = {};

  function decodeEntities(value) {
    var textarea = document.createElement('textarea');
    textarea.innerHTML = String(value == null ? '' : value);
    return textarea.value;
  }

  var text = {
    empty: decodeEntities(root.dataset.textEmpty || 'Your cart is empty.'),
    freeAway: decodeEntities(root.dataset.textFreeShippingAway || "You're {amount} away from free shipping"),
    freeUnlocked: decodeEntities(root.dataset.textFreeShippingUnlocked || 'You have unlocked free shipping!'),
    add: decodeEntities(root.dataset.textAdd || 'ADD'),
    selectSize: decodeEntities(root.dataset.textSelectSize || 'Select size'),
    remove: decodeEntities(root.dataset.textRemove || 'Remove'),
    decrease: decodeEntities(root.dataset.textDecrease || 'Decrease quantity'),
    increase: decodeEntities(root.dataset.textIncrease || 'Increase quantity')
  };

  // Defensive fix for an older English translation that may still exist in Shopify.
  if ((document.documentElement.lang || '').toLowerCase().indexOf('en') === 0) {
    text.freeUnlocked = 'You have unlocked free shipping!';
  }

  /*
   * FREE SHIPPING FX — V7
   * ---------------------
   * Country -> EUR threshold is selected by Liquid.
   * Non-EUR thresholds are converted online using Frankfurter's latest EUR FX rate.
   * Successful rates are cached locally for 12 hours.
   */
  var FX_CACHE_TTL = 12 * 60 * 60 * 1000;
  var FX_CACHE_PREFIX = 'scd_fx_EUR_';
  var fxMemoryCache = {};

  function readFxCache(currency, allowExpired) {
    currency = String(currency || '').toUpperCase();
    if (!currency || currency === 'EUR') return null;

    if (fxMemoryCache[currency]) {
      var mem = fxMemoryCache[currency];
      if (allowExpired || (Date.now() - mem.savedAt < FX_CACHE_TTL)) return mem;
    }

    try {
      var raw = localStorage.getItem(FX_CACHE_PREFIX + currency);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || !Number(parsed.rate)) return null;

      fxMemoryCache[currency] = parsed;

      if (allowExpired || (Date.now() - Number(parsed.savedAt || 0) < FX_CACHE_TTL)) {
        return parsed;
      }
    } catch (_) {}

    return null;
  }

  function saveFxCache(currency, rate, date) {
    var payload = {
      rate: Number(rate),
      date: date || '',
      savedAt: Date.now()
    };

    fxMemoryCache[currency] = payload;

    try {
      localStorage.setItem(FX_CACHE_PREFIX + currency, JSON.stringify(payload));
    } catch (_) {}

    return payload;
  }

  function fetchFxRate(currency) {
    currency = String(currency || '').toUpperCase();

    if (currency === 'EUR') {
      return Promise.resolve({
        rate: 1,
        date: '',
        source: 'eur'
      });
    }

    var fresh = readFxCache(currency, false);
    if (fresh) {
      return Promise.resolve({
        rate: Number(fresh.rate),
        date: fresh.date || '',
        source: 'cache'
      });
    }

    var url = 'https://api.frankfurter.dev/v2/rate/EUR/' + encodeURIComponent(currency);

    return fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('FX request failed: ' + response.status);
      return response.json();
    }).then(function (data) {
      var rate = Number(data && data.rate);
      if (!rate || !isFinite(rate)) throw new Error('Invalid FX rate');

      saveFxCache(currency, rate, data.date || '');

      return {
        rate: rate,
        date: data.date || '',
        source: 'frankfurter'
      };
    }).catch(function (error) {
      var stale = readFxCache(currency, true);

      if (stale) {
        return {
          rate: Number(stale.rate),
          date: stale.date || '',
          source: 'stale-cache'
        };
      }

      console.warn('Shameless cart FX fallback:', error);

      return {
        rate: null,
        date: '',
        source: 'nominal-fallback'
      };
    });
  }

  function thresholdConfig(cart) {
    var country = String(root.dataset.country || '').toUpperCase();
    var storefrontCurrency = String(
      (cart && cart.currency) || root.dataset.currency || 'EUR'
    ).toUpperCase();

    var baseEurCents = Number(root.dataset.thresholdEurCents || 0);
    var group = String(root.dataset.thresholdGroup || '');

    if (storefrontCurrency === 'EUR') {
      return Promise.resolve({
        cents: baseEurCents,
        currency: 'EUR',
        country: country,
        group: group,
        rate: 1,
        rateDate: '',
        source: 'exact-eur-rule'
      });
    }

    return fetchFxRate(storefrontCurrency).then(function (fx) {
      if (fx.rate) {
        var convertedCents = Math.max(1, Math.round(baseEurCents * Number(fx.rate)));

        return {
          cents: convertedCents,
          currency: storefrontCurrency,
          country: country,
          group: group,
          rate: Number(fx.rate),
          rateDate: fx.date || '',
          source: fx.source || 'frankfurter'
        };
      }

      return {
        cents: baseEurCents,
        currency: storefrontCurrency,
        country: country,
        group: group,
        rate: null,
        rateDate: '',
        source: 'nominal-fallback'
      };
    });
  }

  function route(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + path.replace(/^\//, '');
  }

  function money(cents, currency) {
    var value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || (currentCart && currentCart.currency) || 'EUR'
      }).format(value);
    } catch (_) {
      return value.toFixed(2) + ' ' + (currency || '');
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeImage(src) {
    if (!src) return '';
    return src.indexOf('//') === 0 ? window.location.protocol + src : src;
  }

  function appendWidth(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function cartImage(item) {
    if (item && item._scd_product_image) return item._scd_product_image;
    if (!item || !item.image) return '';
    return normalizeImage(
      typeof item.image === 'string'
        ? item.image
        : (item.image.src || item.image.url || '')
    );
  }

  // Prefer the merchant's PRODUCT_ file; ignore CDN query strings and case.
  function productPhoto(product) {
    if (!product || !product.images) return '';
    function key(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    var expected = 'product' + key(product.title);
    var first = '';
    var exact = '';
    product.images.forEach(function (image) {
      var src = typeof image === 'string' ? image : (image.src || image.url || '');
      var name = src.split('?')[0].split('/').pop() || '';
      try { name = decodeURIComponent(name); } catch (error) { /* Keep original filename. */ }
      name = name.replace(/\.[^.]+$/, '');
      if (!/^product_/i.test(name)) return;
      if (!first) first = src;
      if (key(name) === expected) exact = src;
    });
    return normalizeImage(exact || first);
  }

  function productImage(product) {
    var preferred = productPhoto(product);
    if (preferred) return preferred;
    var src = '';
    if (product) {
      if (typeof product.featured_image === 'string') {
        src = product.featured_image;
      } else if (product.featured_image && product.featured_image.url) {
        src = product.featured_image.url;
      } else if (product.featured_image && product.featured_image.src) {
        src = product.featured_image.src;
      } else if (product.images && product.images.length) {
        src = typeof product.images[0] === 'string'
          ? product.images[0]
          : (product.images[0].src || product.images[0].url || '');
      }
    }
    return normalizeImage(src);
  }

  function updateHeaderCount(count) {
    document.querySelectorAll('#site-control .cart:not(.nav-search) > div').forEach(function (el) {
      el.textContent = count;
    });
  }

  function renderShipping(cart) {
    if (!cart || cart.item_count < 1) {
      shippingEl.hidden = true;
      shippingEl.classList.remove('is-unlocked');
      return Promise.resolve(null);
    }

    shippingEl.hidden = true;

    return thresholdConfig(cart).then(function (config) {
      var threshold = Number(config.cents || 0);

      root.dataset.activeThresholdCents = String(threshold || '');
      root.dataset.activeThresholdCurrency = config.currency || '';
      root.dataset.activeThresholdSource = config.source || '';
      root.dataset.activeThresholdGroup = config.group || '';
      root.dataset.activeThresholdRate = config.rate ? String(config.rate) : '';
      root.dataset.activeThresholdRateDate = config.rateDate || '';

      if (!threshold) {
        shippingEl.hidden = true;
        shippingEl.classList.remove('is-unlocked');
        return config;
      }

      shippingEl.hidden = false;

      var total = Number(cart.total_price || 0);
      var remaining = Math.max(0, threshold - total);
      var pct = Math.max(0, Math.min(100, (total / threshold) * 100));

      if (remaining > 0) {
        shippingEl.classList.remove('is-unlocked');
        var amount = money(remaining, cart.currency);
        shippingTextEl.innerHTML = text.freeAway.replace(
          '{amount}',
          '<strong>' + escapeHtml(amount) + '</strong>'
        );
      } else {
        shippingEl.classList.add('is-unlocked');
        shippingTextEl.textContent = text.freeUnlocked;
      }

      shippingProgressEl.style.width = pct + '%';
      return config;
    });
  }

  function itemOptions(item) {
    var values = item.options_with_values || [];

    if (!values.length && item.variant_title && item.variant_title !== 'Default Title') {
      return '<div>' + escapeHtml(item.variant_title) + '</div>';
    }

    return values.map(function (opt) {
      if (!opt || opt.value === 'Default Title' || opt.name === 'Title') return '';
      return '<div>' + escapeHtml(opt.name) + ' ' + escapeHtml(opt.value) + '</div>';
    }).join('');
  }

  function productHandleFromItem(item) {
    if (!item) return '';
    if (item.handle) return item.handle;

    var url = item.url || '';
    var match = url.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : '';
  }

  function fetchProductJson(handle) {
    if (!handle) return Promise.resolve(null);

    if (productJsonCache[handle]) {
      return Promise.resolve(productJsonCache[handle]);
    }

    return fetch(route('products/' + encodeURIComponent(handle) + '.js'), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Product JSON request failed');
      return response.json();
    }).then(function (product) {
      productJsonCache[handle] = product;
      return product;
    }).catch(function () {
      return null;
    });
  }

  function hydrateCompareAtPrices(cart) {
    if (!cart || !cart.items || !cart.items.length) {
      return Promise.resolve(cart);
    }

    var jobs = cart.items.map(function (item) {
      var handle = productHandleFromItem(item);

      return fetchProductJson(handle).then(function (product) {
        if (!product) return;
        item._scd_product_image = productPhoto(product);
        if (!product.variants) return;

        var variant = product.variants.find(function (candidate) {
          return Number(candidate.id) === Number(item.variant_id || item.id);
        });

        if (!variant) return;

        var compareAt = Number(variant.compare_at_price || 0);
        var sellingPrice = Number(item.final_price || item.price || 0);

        if (compareAt > sellingPrice) {
          item._scd_compare_at_price = compareAt;
        }
      });
    });

    return Promise.all(jobs).then(function () {
      return cart;
    });
  }

  function cartTitle(value) {
    return String(value || '').toLocaleLowerCase().replace(/(^|\s)(\S)/g, function (match, space, letter) {
      return space + letter.toLocaleUpperCase();
    });
  }

  function quantityIcon(increase) {
    return '<svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10' +
      (increase ? 'M8 3v10' : '') + '" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
  }

  function renderItems(cart) {
    if (!cart || !cart.items || !cart.items.length) {
      itemsEl.innerHTML = '';
      emptyEl.hidden = false;
      footerEl.hidden = true;
      shippingEl.hidden = true;
      recsEl.hidden = true;
      closeVariantPicker();
      return;
    }

    emptyEl.hidden = true;
    footerEl.hidden = false;

    itemsEl.innerHTML = cart.items.map(function (item) {
      var image = cartImage(item);
      var currentPrice = Number(item.final_price || item.price || 0);
      var oldPrice = Math.max(
        Number(item._scd_compare_at_price || 0),
        Number(item.original_price || 0)
      );
      var hasOld = oldPrice > currentPrice;

      return [
        '<article class="scd__item" data-key="' + escapeHtml(item.key) + '">',
          '<a class="scd__item-image" href="' + escapeHtml(item.url || '#') + '">',
            image
              ? '<img src="' + escapeHtml(appendWidth(image, 320)) + '" alt="' + escapeHtml(item.product_title || '') + '" loading="lazy">'
              : '',
          '</a>',
          '<div class="scd__item-body">',
            '<div class="scd__item-top">',
              '<h3 class="scd__item-title"><a href="' + escapeHtml(item.url || '#') + '">' + escapeHtml(cartTitle(item.product_title)) + '</a></h3>',
              '<button class="scd__remove" type="button" data-scd-remove data-key="' + escapeHtml(item.key) + '">' + escapeHtml(text.remove) + '</button>',
            '</div>',
            '<div class="scd__item-price">',
              hasOld ? '<s>' + escapeHtml(money(oldPrice, cart.currency)) + '</s>' : '',
              '<span>' + escapeHtml(money(currentPrice, cart.currency)) + '</span>',
            '</div>',
            '<div class="scd__item-options">' + itemOptions(item) + '</div>',
            '<div class="scd__qty">',
              '<button type="button" data-scd-qty="-1" data-key="' + escapeHtml(item.key) + '" data-qty="' + item.quantity + '" aria-label="' + escapeHtml(text.decrease) + '">' + quantityIcon(false) + '</button>',
              '<span>' + item.quantity + '</span>',
              '<button type="button" data-scd-qty="1" data-key="' + escapeHtml(item.key) + '" data-qty="' + item.quantity + '" aria-label="' + escapeHtml(text.increase) + '">' + quantityIcon(true) + '</button>',
            '</div>',
          '</div>',
        '</article>'
      ].join('');
    }).join('');

    var currentTotal = Number(cart.total_price || 0);
    var originalTotal = Number(cart.original_total_price || 0);

    var compareAtSubtotal = cart.items.reduce(function (sum, item) {
      var unitOriginal = Math.max(
        Number(item._scd_compare_at_price || 0),
        Number(item.original_price || 0),
        Number(item.final_price || item.price || 0)
      );
      return sum + (unitOriginal * Number(item.quantity || 0));
    }, 0);

    originalTotal = Math.max(originalTotal, compareAtSubtotal);

    if (originalTotal > currentTotal) {
      subtotalEl.innerHTML =
        '<span class="scd__subtotal-prices">' +
          '<s class="scd__subtotal-old">' + escapeHtml(money(originalTotal, cart.currency)) + '</s>' +
          '<span class="scd__subtotal-current">' + escapeHtml(money(currentTotal, cart.currency)) + '</span>' +
        '</span>';
    } else {
      subtotalEl.innerHTML =
        '<span class="scd__subtotal-current">' + escapeHtml(money(currentTotal, cart.currency)) + '</span>';
    }
  }

  function fetchCart() {
    return fetch(route('cart.js'), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    });
  }

  function refresh(options) {
    options = options || {};

    return fetchCart().then(function (cart) {
      return hydrateCompareAtPrices(cart);
    }).then(function (cart) {
      currentCart = cart;
      updateHeaderCount(cart.item_count || 0);
      renderItems(cart);
      renderShipping(cart);

      var newSeed = cart.items && cart.items.length ? cart.items[0].product_id : null;

      if (newSeed && (newSeed !== recSeedProductId || options.forceRecommendations)) {
        recSeedProductId = newSeed;
        loadRecommendations(newSeed);
      } else if (!newSeed) {
        recsEl.hidden = true;
        recsTrack.innerHTML = '';
        recSeedProductId = null;
      }

      return cart;
    }).catch(function (error) {
      console.warn('Shameless cart drawer:', error);
    });
  }

  function changeLine(key, quantity) {
    return fetch(route('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: Math.max(0, quantity)
      })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (data) { throw data; });
      }
      return response.json();
    }).then(function (cart) {
      return hydrateCompareAtPrices(cart);
    }).then(function (cart) {
      currentCart = cart;
      updateHeaderCount(cart.item_count || 0);
      renderItems(cart);
      renderShipping(cart);

      if (!cart.item_count) {
        recsEl.hidden = true;
        recsTrack.innerHTML = '';
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));

      return cart;
    }).catch(function (error) {
      console.warn('Shameless cart drawer change:', error);
      return refresh();
    });
  }

  function addVariant(variantId) {
    return fetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1
        }]
      })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (data) { throw data; });
      }
      return response.json();
    }).then(function () {
      closeVariantPicker();
      return refresh({ forceRecommendations: true });
    }).catch(function (error) {
      console.warn('Shameless recommendation add:', error);
    });
  }

  function recommendationVariants(product) {
    return ((product && product.variants) || []).filter(function (variant) {
      return variant && variant.available !== false;
    });
  }

  function variantLabel(variant) {
    var label = variant.public_title || variant.title || '';

    if (label === 'Default Title') return '';

    if (label.indexOf(' / ') > -1) {
      var parts = label.split(' / ');
      label = parts[parts.length - 1];
    }

    return label;
  }

  function recommendationCard(product) {
    var image = productImage(product);
    var url = product.url || ('/products/' + (product.handle || ''));
    var price = product.price_min != null ? product.price_min : product.price;
    var compareAt = product.compare_at_price_min != null
      ? product.compare_at_price_min
      : product.compare_at_price;
    var variants = recommendationVariants(product);
    var immediate = variants.length === 1;
    var variantId = immediate ? variants[0].id : '';

    return [
      '<article class="scd__rec" data-product-url="' + escapeHtml(url) + '">',
        '<div class="scd__rec-media">',
          image
            ? '<img src="' + escapeHtml(appendWidth(image, 360)) + '" alt="' + escapeHtml(product.title || '') + '" loading="lazy">'
            : '',
          '<button class="scd__rec-add" type="button" aria-label="' + escapeHtml(text.selectSize + ': ' + (product.title || '')) + '" data-scd-rec-add data-product-id="' + escapeHtml(product.id) + '" data-variant-id="' + escapeHtml(variantId) + '"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 18 18" fill="none"><path d="M9 3v12M3 9h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>',
        '</div>',
        '<div class="scd__rec-title">' + escapeHtml(cartTitle(product.title)) + '</div>',
        '<div class="scd__rec-price">' +
          (Number(compareAt || 0) > Number(price || 0)
            ? '<s>' + escapeHtml(money(compareAt, currentCart && currentCart.currency)) + '</s>'
            : '') +
          '<span>' + escapeHtml(money(price, currentCart && currentCart.currency)) + '</span>' +
        '</div>',
      '</article>'
    ].join('');
  }

  function loadRecommendations(productId) {
    var token = ++recRequestToken;
    recommendationProducts = {};
    recsEl.hidden = true;
    recsTrack.innerHTML = '';

    fetch(
      route('recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=7&intent=related'),
      {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }
    ).then(function (response) {
      if (!response.ok) throw new Error('Recommendations request failed');
      return response.json();
    }).then(function (data) {
      if (token !== recRequestToken) return;

      var products = (data && data.products) || [];
      var cartProductIds = (currentCart && currentCart.items ? currentCart.items : []).map(function (item) {
        return Number(item.product_id);
      });

      products = products.filter(function (product) {
        return product && cartProductIds.indexOf(Number(product.id)) === -1;
      }).slice(0, 7);

      if (!products.length) {
        recsEl.hidden = true;
        return;
      }

      products.forEach(function (product) {
        recommendationProducts[String(product.id)] = product;
      });

      recsTrack.innerHTML = products.map(recommendationCard).join('');
      recsEl.hidden = false;
    }).catch(function (error) {
      console.warn('Shameless recommendations:', error);
      recsEl.hidden = true;
    });
  }

  function openVariantPicker(button) {
    var product = recommendationProducts[String(button.dataset.productId)];
    if (!product) return;

    var variants = recommendationVariants(product);

    if (variants.length === 1) {
      addVariant(variants[0].id);
      return;
    }

    variantProductEl.textContent = product.title || '';
    variantOptionsEl.innerHTML = variants.map(function (variant) {
      var label = variantLabel(variant) || text.selectSize;
      return (
        '<button class="scd__size" type="button" data-scd-size data-variant-id="' +
        escapeHtml(variant.id) +
        '">' +
        escapeHtml(label) +
        '</button>'
      );
    }).join('');

    variantLayer.hidden = false;
  }

  function closeVariantPicker() {
    if (!variantLayer) return;
    variantLayer.hidden = true;
    if (variantOptionsEl) variantOptionsEl.innerHTML = '';
    if (variantProductEl) variantProductEl.textContent = '';
  }

  function open(options) {
    options = options || {};
    lastFocused = document.activeElement;

    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('scd-open');

    document.querySelectorAll('[data-shameless-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-expanded', 'true');
    });

    refresh({
      forceRecommendations: !!options.forceRecommendations
    });

    window.setTimeout(function () {
      var close = root.querySelector('.scd__close');
      if (close) close.focus({ preventScroll: true });
    }, 40);
  }

  function close() {
    closeVariantPicker();

    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('scd-open');

    document.querySelectorAll('[data-shameless-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-expanded', 'false');
    });

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }
  }

  root.addEventListener('click', function (event) {
    var variantClose = event.target.closest('[data-scd-variant-close]');
    if (variantClose) {
      event.preventDefault();
      closeVariantPicker();
      return;
    }

    var closeBtn = event.target.closest('[data-scd-close]');
    if (closeBtn) {
      event.preventDefault();
      close();
      return;
    }

    var removeBtn = event.target.closest('[data-scd-remove]');
    if (removeBtn) {
      event.preventDefault();
      changeLine(removeBtn.dataset.key, 0);
      return;
    }

    var qtyBtn = event.target.closest('[data-scd-qty]');
    if (qtyBtn) {
      event.preventDefault();

      var current = Number(qtyBtn.dataset.qty || 0);
      var next = current + Number(qtyBtn.dataset.scdQty || 0);

      changeLine(qtyBtn.dataset.key, next);
      return;
    }

    var recAdd = event.target.closest('[data-scd-rec-add]');
    if (recAdd) {
      event.preventDefault();
      event.stopPropagation();

      if (recAdd.dataset.variantId) {
        addVariant(recAdd.dataset.variantId);
      } else {
        openVariantPicker(recAdd);
      }
      return;
    }

    var sizeBtn = event.target.closest('[data-scd-size]');
    if (sizeBtn) {
      event.preventDefault();
      event.stopPropagation();
      addVariant(sizeBtn.dataset.variantId);
      return;
    }

    var rec = event.target.closest('.scd__rec');
    if (rec && !event.target.closest('button')) {
      window.location.href = rec.dataset.productUrl;
    }
  });

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-shameless-cart-toggle]');
    if (!toggle) return;

    event.preventDefault();
    open();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    if (variantLayer && !variantLayer.hidden) {
      closeVariantPicker();
      return;
    }

    if (root.getAttribute('aria-hidden') === 'false') {
      close();
    }
  });

  document.addEventListener('shameless:cart:open', function () {
    open({ forceRecommendations: true });
  });

  window.ShameLessCartDrawer = {
    open: open,
    close: close,
    refresh: refresh,
    getFreeShippingRule: function () {
      return thresholdConfig(currentCart || {
        currency: root.dataset.currency || 'EUR'
      });
    },
    clearFxCache: function () {
      fxMemoryCache = {};
      try {
        Object.keys(localStorage).forEach(function (key) {
          if (key.indexOf(FX_CACHE_PREFIX) === 0) localStorage.removeItem(key);
        });
      } catch (_) {}
      return refresh();
    }
  };

  /*
   * Performance: do not fetch /cart.js (and product JSON for compare-at prices)
   * on every page load. open() already refreshes the drawer before the customer
   * uses it, and add/change actions refresh after mutations.
   */
})();